import { useEffect } from 'react';
import { shouldPoll } from './fleet-view.js';

/**
 * Quietly refetch every minute while somebody is looking: the collector ticks
 * once a minute, so polling faster would only re-read the same answer.
 *
 * "Looking" is the tab visible AND some activity in the last 30 minutes; see
 * `shouldPoll` for why an unattended screen must stop. The first activity after
 * a pause refetches at once, so coming back does not mean reading stale numbers
 * for up to a minute.
 *
 * Its own module since org-admin#98, so the Routing section (CiRouting.tsx)
 * polls the same way as the sections in CiFleet.tsx.
 */
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel'] as const;

export function usePoll(reload: (quiet?: boolean) => void, everyMs = 60_000) {
  useEffect(() => {
    let lastActivityAt = Date.now();
    const visible = () => document.visibilityState === 'visible';
    const onActivity = () => {
      const wasPaused = !shouldPoll(true, lastActivityAt);
      lastActivityAt = Date.now();
      if (wasPaused && visible()) reload(true);
    };
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, onActivity, { passive: true });
    document.addEventListener('visibilitychange', onActivity);
    const timer = window.setInterval(() => {
      if (shouldPoll(visible(), lastActivityAt)) reload(true);
    }, everyMs);
    return () => {
      window.clearInterval(timer);
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, onActivity);
      document.removeEventListener('visibilitychange', onActivity);
    };
  }, [reload, everyMs]);
}
